import {debounce, truncate} from 'webapp-lib/utils';

import documentsBackend from '@lexian/api/documents-api';
import i18n from '_/i18n';
import {DocumentTypeUnion} from '_/utilities/types';

export const removeUnnecessaryExtension = ({
  fileTitle,
  fileType,
}: {
  fileTitle: string;
  fileType: DocumentTypeUnion | undefined | null;
}): string => {
  if (fileType === undefined || fileType === null) {
    return fileTitle;
  }

  const extensionsToStrip: {[key in DocumentTypeUnion]: string[]} = {
    WORD_DOCUMENT: ['.doc', '.docx'],
    EXCEL_DOCUMENT: ['.xls', '.xlsx'],
    POWERPOINT_DOCUMENT: ['.ppt', '.pptx'],
    PDF_DOCUMENT: ['.pdf'],
    EMAIL_DOCUMENT: ['.eml'],
    IMAGE_DOCUMENT: [
      '.jpg',
      '.png',
      '.bmp',
      '.svg',
      '.gif',
      '.jpeg',
      '.webp',
      '.tiff',
    ],
    RTF_DOCUMENT: ['.rtf'],
    TEXT_DOCUMENT: ['.txt', '.json', '.xml', '.csv', '.yaml'],
    ZIP_DOCUMENT: ['.zip'],
    UNKNOWN_DOCUMENT: [],
  };

  const shouldStrip = extensionsToStrip[fileType].some((ext) =>
    fileTitle.toLowerCase().endsWith(ext)
  );

  if (shouldStrip) {
    return fileTitle.substring(0, fileTitle.lastIndexOf('.'));
  } else {
    return fileTitle;
  }
};

const validateAvailableFileName = async ({
  fileTitle,
  extension,
  type,
  folderId,
  casusId,
  excludedDocumentId,
}: {
  fileTitle: string;
  extension: string | undefined;
  type: DocumentTypeUnion;
  folderId: string | undefined;
  casusId: string | undefined;
  excludedDocumentId?: string;
}): Promise<undefined | string> => {
  if (!fileTitle || !folderId || !casusId) {
    return undefined;
  }

  const itemTitle = removeUnnecessaryExtension({fileTitle, fileType: type});
  const itemExtension = extension ? `.${extension}` : '';
  let pathAvailable: boolean | undefined;
  let hasMicrogatewayError: boolean = false;
  await documentsBackend.checkDocumentNameAvailability({
    parameters: {
      casusId,
      folderId,
      itemName: itemTitle + itemExtension,
      excludedDocumentId,
    },
    onSuccess: ({body}) => {
      pathAvailable = body.available;
    },
    localHandlers: {
      400: () => {
        hasMicrogatewayError = true;
      },
    },
    afterErrorHandler: () => {
      pathAvailable = undefined;
    },
  });

  if (hasMicrogatewayError) {
    return i18n.t(
      'documents:documentNameViolatesMicrogatewayRules',
      'Unerlaubte Zeichenfolge in Dokumentenname'
    );
  }
  return pathAvailable
    ? undefined
    : i18n
        .t('documents:fileExistsHelp', {
          filename: truncate(fileTitle || '', 45) + itemExtension,
        })
        .toString();
};

export const {debouncedFunc: debouncedValidateAvailableFileName} = debounce(
  validateAvailableFileName
);
